import React, { useEffect } from 'react'

import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useRouteMatch } from 'react-router-dom'
import Flex from 'styled-flex-component'

import { SideBarBlock, SideBarBlockItem } from '@components/UI/SideBar'
import { useFlag } from '@context/FeatureToggle'
import { FeatureToggles } from '@enums'
import { IApplicationState } from '@store'
import { fetchAgentSourcedCreatorsCount } from '@store/agentSourcedCreators/actions'
import {
  selectAgentSourcedCreatorsTotal,
  selectInterestedCount,
  selectShortlistShortlistedCount,
} from '@store/agentSourcedCreators/selectors'
import { selectCampaign, selectIsCampaignFetched } from '@store/campaigns'
import { selectMeInfo } from '@store/me'
import { selectCampaignId } from '@store/router'
import { isAdmin, objectToQueryString, queryStringtoObject } from '@utils'

const TABS = [
  { key: 'list' as const, messageId: 'core.text.list' },
  { key: 'shortlisted' as const, messageId: 'core.text.shortlisted' },
]

const CreatorSearchFilters: React.FC = () => {
  const match = useRouteMatch()
  const { pathname, search } = useLocation()
  const { tab } = queryStringtoObject(search) as { tab?: string }
  const campaignId = selectCampaignId(match) as number | undefined
  const basePath = `/campaigns/${campaignId}/creators`
  const isOnCreatorSearch = pathname === basePath
  const currentTab = tab || 'list'

  const dispatch = useDispatch()
  const meInfo = useSelector(selectMeInfo)
  const vibeCheckTotal = useSelector(selectAgentSourcedCreatorsTotal)
  const listAvailableCount = useSelector(selectInterestedCount)
  const shortlistedCount = useSelector(selectShortlistShortlistedCount)
  const isCreatorSourcingEnabled = useFlag(FeatureToggles.CREATOR_SOURCING_AGENT_ENABLED)
  const isCampaignFetched = useSelector((state: IApplicationState) =>
    campaignId ? selectIsCampaignFetched(state, campaignId) : false,
  )
  const campaign = useSelector((state: IApplicationState) =>
    campaignId ? selectCampaign(state, campaignId) : undefined,
  )
  const creatorSourcingEnabledForCampaign = !!campaign?.creator_sourcing_enabled

  const tabCounts: Record<'list' | 'shortlisted', number> = {
    list: listAvailableCount,
    shortlisted: shortlistedCount,
  }

  const showVibeCheck = campaignId && isAdmin(meInfo as any)

  // Warm up the tab counts on mount and when campaignId changes so the sidebar
  // badges are populated without requiring the user to visit each tab. Gated
  // on isCampaignFetched because the interested/shortlisted count sagas
  // resolve their HATEOAS link from the campaign resource — dispatching
  // pre-fetch would just short-circuit to "link not found" failures.
  //
  // BRAND-9582 item 7 removed the vibe-check warm-up from this effect because
  // the saga read its `metadata.total`, which is scoped to the currently
  // SELECTED run rather than all runs — it raced the correct stats-derived
  // all-runs total and won non-deterministically (confirmed live on QA: 55
  // instead of 111).
  //
  // BRAND-9678 restores it: the saga now also forwards `metadata.stats`, and
  // the reducer takes the badge from `stats.vibe_check` (unfiltered, all
  // runs — the same block a real list fetch supplies) while still ignoring
  // the run-scoped `total`. Without a warm-up of its own, vibeCheckTotal had
  // no campaign-scoped refresh path at all on the List/Shortlisted tabs — no
  // vibe-check list fetch happens there — so opening another brief kept
  // showing the previous brief's count until the user clicked into the tab.
  // Staff-gated on the same `showVibeCheck` the badge is: the listing is
  // staff-only on the BE (ensure_tribe_staff!), so a non-admin would only
  // earn a 403, and the badge isn't rendered for them anyway.
  useEffect(() => {
    if (!campaignId || !isCreatorSourcingEnabled || !isCampaignFetched) return
    // BRAND-9678: this component is the shared CAMPAIGN sidebar — it mounts on
    // Inbox/Reports/PCR too, not just the creators page — and it renders
    // nothing at all for a brief without creator sourcing (see the early
    // return below). Since the warm-up now rebases the sticky totals as well
    // as filling them, gating on the same flag keeps a brief that has no
    // badges to populate from resetting another brief's, and keeps the
    // staff-only vibe-check probe off pages that would never show its count.
    if (!creatorSourcingEnabledForCampaign) return
    if (showVibeCheck) {
      dispatch(fetchAgentSourcedCreatorsCount({ campaignId, listing: 'vibe-check' }))
    }
    dispatch(fetchAgentSourcedCreatorsCount({ campaignId, listing: 'interested' }))
    dispatch(fetchAgentSourcedCreatorsCount({ campaignId, listing: 'shortlisted' }))
  }, [
    campaignId,
    isCreatorSourcingEnabled,
    isCampaignFetched,
    showVibeCheck,
    creatorSourcingEnabledForCampaign,
  ])

  if (!isCreatorSourcingEnabled || !creatorSourcingEnabledForCampaign) return null

  return (
    <SideBarBlock
      header={
        <Flex justifyBetween alignCenter data-testid="filter-header-creators">
          <FormattedMessage id="core.text.creators" />
        </Flex>
      }
      items={[
        ...(showVibeCheck
          ? [
              <SideBarBlockItem
                key="vibe-check"
                text={<FormattedMessage id="creatorSearch.vibeCheck.title" />}
                to={{
                  pathname: basePath,
                  search: objectToQueryString({ tab: 'vibe-check' }),
                }}
                active={isOnCreatorSearch && currentTab === 'vibe-check'}
                showNotification={false}
                elementName="side-bar-vibe-check"
                count={vibeCheckTotal > 0 ? vibeCheckTotal : undefined}
              />,
            ]
          : []),
        ...TABS.map(({ key, messageId }) => (
          <SideBarBlockItem
            key={key}
            to={{
              pathname: basePath,
              search: objectToQueryString({ tab: key }),
            }}
            text={<FormattedMessage id={messageId} />}
            active={isOnCreatorSearch && currentTab === key}
            showNotification={false}
            elementName={`side-bar-creator-search-${key}`}
            count={tabCounts[key] > 0 ? tabCounts[key] : undefined}
          />
        )),
      ]}
    />
  )
}

export { CreatorSearchFilters }
export default CreatorSearchFilters
